import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { Logger, concise, formatError } from '../../packages/logger/src/Logger';
import {
  getCrossLaunchToken, getMSTeamsToken
} from '../service/MsTeamsAuthenticationService';

/**
 *
 * @param config
 */
export function requestLoggerInterceptor(
  config: AxiosRequestConfig
): AxiosRequestConfig {
  // Suppress verbose logging for photo, user search, extension, and contact endpoints
  const isPhotoEndpoint = config.url?.includes('/photo/$value');
  const isUserSearch = config.url?.includes('/users?');
  const isExtension = config.url?.includes('/extensions/');
  const isContact = config.url?.includes('/contacts');
  
  if (!isPhotoEndpoint && !isUserSearch && !isExtension && !isContact) {
    Logger.debug(`Start to ${config.method} ${config.baseURL}${config.url}.`);
  }
  return config;
}

/**
 *
 * @param response
 */
export function responseLoggerInterceptor(
  response: AxiosResponse<any, any>
): AxiosResponse<any, any> {
  // Suppress verbose logging for photo, user search, extension, and contact endpoints
  const isPhotoEndpoint = response.config.url?.includes('/photo/$value');
  const isUserSearch = response.config.url?.includes('/users?');
  const isExtension = response.config.url?.includes('/extensions/');
  const isContact = response.config.url?.includes('/contacts');
  
  if (!isPhotoEndpoint && !isUserSearch && !isExtension && !isContact) {
    Logger.debug(
      `End to ${response.config.method} ${response.config.baseURL}${response.config.url}.`
    );
  }
  return response;
}

/**
 *
 * @param error
 */
export function responseErrorLoggerInterceptor(error: any): any {
  if (error.config) {
    const isPhotoEndpoint = error.config.url?.includes('/photo/$value');
    const isContactEndpoint = error.config.url?.includes('/contacts');
    const is404 = error.response?.status === 404;
    const is429 = error.response?.status === 429; // Rate limiting
    
    // Suppress expected errors to reduce noise
    if (isPhotoEndpoint && is404) {
      // Expected: users/contacts without photos
      return error;
    }
    
    if (is429) {
      // Rate limiting is handled by retry logic - don't spam logs
      return error;
    }
    
    // Suppress verbose logging for contact endpoints (creates too much noise during search)
    if (isContactEndpoint) {
      return error;
    }
    
    Logger.error(
      `Fail to ${error.config.method} '${error.config.baseURL}${error.config.url}'. Reason: ${error.message}`
    );
  } else {
    Logger.error(formatError(error));
  }
  // return Promise.reject(error);
  return error;
}

export async function addGraphAuthorizationInterceptor(
  config: AxiosRequestConfig
): Promise<AxiosRequestConfig> {
  try {
    const accessToken = await getMSTeamsToken(false);
    // const accessToken = getLocalStorageItem<string>('msAccessTokenDecrypt')
    // ! This is supposed to be handled by getMSTeamsToken
    if (!accessToken) {
      Logger.error('access token is null');
      return await Promise.reject(new Error('No access token'));
    }
    Logger.debug(
      `GraphClient.setAuthorizationHeader(): set Authorization header: 'Bearer ${concise(
        accessToken
      )}'.`
    );
    if (!config.headers) {
      config.headers = {}; // eslint-disable-line
    }
    config.headers.Authorization = `Bearer ${accessToken}`; // eslint-disable-line
    return config;
  } catch (error) {
    Logger.error(
      `GraphClient.setAuthorizationHeader(): got error: '${formatError(
        error
      )}'.`
    );
    // ! This is supposed to be handled by getMSTeamsToken
    // localStorage.clear();
    throw error;
  }
}

export async function addCrossLaunchAuthorizationInterceptor(
  config: AxiosRequestConfig
) {
  // cross launch call token has the same life cycle as access token (Graph API token)
  try {
    const crossLaunchCallToken = await getCrossLaunchToken();
    Logger.debug(
      `WebexClient.setAuthorizationHeader(): set Authorization header: 'Bearer ${concise(
        crossLaunchCallToken
      )}'.`
    );
    // ! Headers are option, making sure they exist before setting Authorization Header
    if (!config.headers) {
      config.headers = {}; // eslint-disable-line
    }
    config.headers.Authorization = `Bearer ${crossLaunchCallToken}`; // eslint-disable-line
    return config;
  } catch (error) {
    Logger.error(
      `WebexClient.setAuthorizationHeader(): got error: '${formatError(
        error
      )}'.`
    );
    localStorage.clear();
    throw error;
  }
}

